<template>
  <div class="security-settings-container">
    <!-- Edit Mode -->
    <div v-if="isEdit" class="edit-mode-container">
      <!-- Email & Password Section -->
      <section
        class="email-password-section"
        aria-labelledby="email-password-heading"
      >
        <h2 id="email-password-heading" class="section-title">
          Email & Password
        </h2>
        <div class="email-password-content">
          <!-- Email Field -->
          <div class="field-container">
            <p id="label-email" class="field-label">Email</p>
            <BaseInput
              v-model="email"
              type="text"
              disabled
              aria-labelledby="label-email"
            />
            <p class="field-helper-text">
              If you would like to change your email, please email
              hello@eddy4teachers.com
            </p>
          </div>

          <!-- Password Fields -->
          <div class="password-fields-container">
            <div v-if="user?.has_password" class="field-container">
              <p id="label-old-password" class="field-label">Old Password</p>
              <BaseInput
                v-model="oldPassword"
                type="password"
                class="password-input"
                aria-labelledby="label-old-password"
              />
            </div>

            <div class="field-container">
              <p id="label-new-password" class="field-label">New Password</p>
              <div class="password-input-group">
                <BaseInput
                  v-model="newPassword"
                  @input="validatePasswordInput"
                  type="password"
                  :error="isPasswordError"
                  :errorMessage="passwordErrorMessage"
                  class="password-input"
                  aria-labelledby="label-new-password"
                />
                <PasswordValidationList
                  v-if="newPassword"
                  :password="newPassword"
                  :validations="validations"
                />
              </div>
            </div>

            <div class="field-container">
              <p id="label-confirm-password" class="field-label">
                Confirm New Password
              </p>
              <BaseInput
                v-model="confirmNewPassword"
                type="password"
                :error="isConfirmPasswordError"
                :errorMessage="confirmPasswordErrorMessage"
                :showInputError="false"
                class="password-input"
                aria-labelledby="label-confirm-password"
              />
            </div>
          </div>

          <div v-if="errorMessage" class="error-message-text">
            {{ errorMessage }}
          </div>
        </div>
      </section>

      <!-- Action Buttons -->
      <div class="edit-actions">
        <ActionButton
          variant="secondary"
          @click="isEdit = false"
          class="security-action-button action-button-cancel"
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant="primary"
          @click="handlePasswordChangeClick"
          :loading="isLoading"
          :disabled="
            (user?.has_password && !oldPassword) ||
            !newPassword ||
            !confirmNewPassword ||
            isPasswordError
          "
          class="security-action-button action-button-save"
        >
          Save
        </ActionButton>
      </div>
    </div>

    <!-- Non-Edit Mode -->
    <div v-else class="non-edit-mode-container">
      <!-- Primary Email Section -->
      <section
        class="primary-email-section"
        aria-labelledby="primary-email-heading"
      >
        <h2 id="primary-email-heading" class="section-title">Primary Email</h2>

        <!-- Information Banner -->
        <div class="info-banner">
          <div class="info-banner-content">
            <div class="info-icon">
              <img
                src="/images/icon/info-circle-icon.svg"
                alt=""
                aria-hidden="true"
              />
            </div>
            <p class="info-text">
              This email will be used to receive important information and
              updates.
            </p>
          </div>
        </div>

        <!-- Email Field with Button -->
        <div class="account-container">
          <div class="account-field">
            <img
              :src="primaryEmailIcon"
              alt=""
              aria-hidden="true"
              class="account-field-icon"
            />
            <p class="account-field-text">{{ email || "No email set" }}</p>
          </div>
          <ActionButton
            variant="secondary"
            @click="openChangeEmailAccountModal"
            :disabled="!hasAvailableAccounts"
            class="security-action-button"
          >
            Change
          </ActionButton>
        </div>
      </section>

      <!-- Divider -->
      <div class="section-divider"></div>

      <!-- Sign In Methods Section -->
      <section
        class="sign-in-methods-section"
        aria-labelledby="sign-in-methods-heading"
      >
        <div class="section-header-container">
          <h2 id="sign-in-methods-heading" class="section-title">
            Sign In Methods
          </h2>
          <p class="section-description">
            Add more than one sign-in method to avoid getting locked out.
          </p>
        </div>

        <div class="connected-accounts-list">
          <!-- Google Account -->
          <div class="account-container">
            <div class="account-field">
              <img
                src="/images/google-logo.svg"
                alt=""
                aria-hidden="true"
                class="account-field-icon"
              />
              <p class="account-field-text">
                {{ googleAccountStatusText }}
              </p>
            </div>
            <ActionButton
              v-if="!isGoogleConnected.isConnected"
              @click="loginWithGoogle"
              :disabled="isLoading"
              variant="secondary"
              class="security-action-button"
            >
              Add Account
            </ActionButton>
            <template v-else>
              <CustomTooltip
                v-if="isGooglePrimaryAndDisabled"
                text="You cannot disconnect the primary email address."
                placement="top"
              >
                <template #trigger>
                  <ActionButton
                    variant="secondary"
                    @click="handleGoogleAccountClick"
                    class="security-action-button disconnect-button"
                    :disabled="!canDisconnectGoogle"
                  >
                    Disconnect
                    <template #icon-right>
                      <XIcon
                        :color="!canDisconnectGoogle ? '#CCCCCC' : '#C21D1D'"
                      />
                    </template>
                  </ActionButton>
                </template>
              </CustomTooltip>
              <ActionButton
                v-else
                variant="secondary"
                @click="handleGoogleAccountClick"
                class="security-action-button disconnect-button"
                :disabled="!canDisconnectGoogle"
              >
                Disconnect
                <template #icon-right>
                  <XIcon
                    :color="!canDisconnectGoogle ? '#CCCCCC' : '#C21D1D'"
                  />
                </template>
              </ActionButton>
            </template>
          </div>

          <!-- Divider between accounts -->
          <div class="account-divider"></div>

          <!-- Email Account -->
          <div class="account-container">
            <div class="account-field">
              <img
                src="/images/email-icon.svg"
                alt=""
                aria-hidden="true"
                class="account-field-icon"
              />
              <p class="account-field-text">
                {{ emailAccountStatusText }}
              </p>
            </div>

            <template v-if="!isConnectedEmail.isConnected">
              <ActionButton
                @click="openChangeEmailModal"
                :disabled="isLoading"
                variant="secondary"
                class="security-action-button"
              >
                Add Account
              </ActionButton>
            </template>
            <template v-else>
              <div class="password-change-container">
                <ActionButton
                  :disabled="isLoading"
                  variant="secondary"
                  @click="togglePasswordForm"
                  class="security-action-button"
                >
                  Change Password
                  <template #icon-right>
                    <ChevronDownIcon
                      :class="{ 'chevron-rotated': showPasswordForm }"
                    />
                  </template>
                </ActionButton>
                <CustomTooltip
                  v-if="isEmailPrimaryAndDisabled"
                  text="You cannot disconnect the primary email address."
                  placement="top"
                >
                  <template #trigger>
                    <ActionButton
                      variant="secondary"
                      @click="handleDisconnectEmailClick"
                      class="security-action-button disconnect-button"
                      :disabled="!canDisconnectEmail"
                    >
                      Disconnect
                      <template #icon-right>
                        <XIcon
                          :color="!canDisconnectEmail ? '#CCCCCC' : '#C21D1D'"
                        />
                      </template>
                    </ActionButton>
                  </template>
                </CustomTooltip>
                <ActionButton
                  v-else
                  variant="secondary"
                  @click="handleDisconnectEmailClick"
                  class="security-action-button disconnect-button"
                  :disabled="!canDisconnectEmail"
                >
                  Disconnect
                  <template #icon-right>
                    <XIcon
                      :color="!canDisconnectEmail ? '#CCCCCC' : '#C21D1D'"
                    />
                  </template>
                </ActionButton>
              </div>
            </template>
          </div>
        </div>

        <!-- Password Change Form -->
        <div
          v-if="showPasswordForm && isConnectedEmail.isConnected"
          class="password-form-container"
        >
          <!-- Info Banner -->
          <div class="password-info-banner">
            <div class="info-banner-content">
              <div class="info-icon">
                <QuestionMarkCircleIcon />
              </div>
              <p class="info-text">
                You can update your password for this sign-in option
              </p>
            </div>
          </div>

          <!-- Password Fields -->
          <div class="password-form-fields">
            <div v-if="user?.has_password" class="field-container">
              <p id="label-change-old-password" class="field-label">
                Old Password
              </p>
              <BaseInput
                v-model="changePasswordOldPassword"
                type="password"
                class="password-input"
                aria-labelledby="label-change-old-password"
              />
            </div>

            <div class="field-container">
              <p id="label-change-new-password" class="field-label">
                New Password
              </p>
              <div class="password-input-group">
                <BaseInput
                  v-model="changePasswordNewPassword"
                  @input="validateChangePasswordInput"
                  type="password"
                  :error="isChangePasswordError"
                  :errorMessage="changePasswordErrorMessage"
                  class="password-input"
                  aria-labelledby="label-change-new-password"
                />
                <PasswordValidationList
                  v-if="changePasswordNewPassword"
                  :password="changePasswordNewPassword"
                  :validations="changePasswordValidations"
                />
              </div>
            </div>

            <div class="field-container">
              <p id="label-change-confirm-password" class="field-label">
                Confirm New Password
              </p>
              <BaseInput
                v-model="changePasswordConfirm"
                @input="validateChangePasswordConfirm"
                type="password"
                :error="isChangePasswordConfirmError"
                :errorMessage="changePasswordConfirmErrorMessage"
                :showInputError="false"
                class="password-input"
                aria-labelledby="label-change-confirm-password"
              />
            </div>
          </div>

          <div v-if="changePasswordErrorMessage" class="error-message-text">
            {{ changePasswordErrorMessage }}
          </div>

          <!-- Save Button -->
          <div class="password-form-actions">
            <ActionButton
              variant="primary"
              @click="handleChangePasswordSave"
              :loading="isLoading"
              :disabled="
                (user?.has_password && !changePasswordOldPassword) ||
                !changePasswordNewPassword ||
                !changePasswordConfirm ||
                isChangePasswordError ||
                isChangePasswordConfirmError
              "
              class="security-action-button"
            >
              Save
            </ActionButton>
          </div>
        </div>
      </section>

      <p v-if="ssoErrorMessage" class="sso-error-message">
        {{ ssoErrorMessage }}
      </p>
    </div>
  </div>

  <ConfirmationModal
    v-model="isDeleteAccount"
    title="Confirmation"
    :message="`Are you sure you want to disconnect your ${disconnectAccountType} account?`"
    confirm-text="Disconnect"
    cancel-text="Back"
    variant="danger"
    :loading="isLoading"
    @confirm="deleteAccount"
    @cancel="isDeleteAccount = false"
  />

  <ConfirmationModal
    v-model="showPasswordChangeConfirmation"
    title="Confirmation"
    :message="passwordChangeConfirmationMessage"
    confirm-text="Continue"
    cancel-text="Back"
    variant="default"
    :loading="isLoading"
    @confirm="changePassword"
    @cancel="showPasswordChangeConfirmation = false"
  />

  <v-dialog v-model="showEmailRequiredWarning" style="width: 500px">
    <v-card style="border-radius: 20px">
      <v-card-title
        style="
          display: flex;
          justify-content: space-between;
          align-items: center;
        "
      >
        <h2
          style="color: #003d6a; font-size: var(--fs-large); font-weight: 600"
        >
          Email Connection Required
        </h2>
        <button
          style="
            color: #e00069;
            font-size: var(--fs-large);
            font-weight: 600;
            cursor: pointer;
            background: none;
            border: none;
          "
          aria-label="Close"
          @click="showEmailRequiredWarning = false"
        >
          ×
        </button>
      </v-card-title>

      <div style="width: 100%; border-top: 1px solid #d8dae5"></div>

      <v-card-text>
        <div style="text-align: center">
          <img
            src="/images/email-icon.svg"
            alt=""
            aria-hidden="true"
            width="80px"
          />

          <p
            style="
              font-size: 18px;
              font-weight: 400;
              color: var(--primary-blue);
              margin-top: 10px;
            "
          >
            Before removing your Google account, you need to set up an
            email/password login.
          </p>

          <p style="font-size: var(--fs-medium); color: #666; margin-top: 10px">
            This ensures you'll still be able to access your account after
            Google login is removed.
          </p>
        </div>

        <div
          style="
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-top: 25px;
          "
        >
          <BaseButton
            variant="secondary"
            @click="showEmailRequiredWarning = false"
          >
            Cancel
          </BaseButton>

          <button
            style="
              color: white;
              background-color: var(--primary-blue);
              font-size: var(--fs-medium);
              padding: 10px 20px;
              border-radius: 16px;
              font-weight: 600;
              border: none;
              cursor: pointer;
            "
            @click="handleConnectEmailRedirect"
          >
            Connect Email
          </button>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <AddEmailModal v-model="showAddEmailModal" @save="handleChangeEmailSave" />

  <ChangeEmailModal
    v-model="showChangeEmailModal"
    :user="user"
    @continue="handleChangeEmailContinue"
  />
</template>

<script setup>
/* eslint-disable */
import authService from "@/services/authService";
import cookiesService from "@/services/cookiesService";
import userService from "@/services/userService";
import { validatePassword } from "@/utils";
import { computed, inject, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import BaseInput from "../BaseInput.vue";
import ActionButton from "../common/ActionButton.vue";
import PasswordValidationList from "../PasswordValidationList.vue";
import ConfirmationModal from "../common/ConfirmationModal.vue";
import XIcon from "../icons/XIcon.vue";
import BaseButton from "../common/BaseButton.vue";
import AddEmailModal from "./AddEmailModal.vue";
import ChangeEmailModal from "./ChangeEmailModal.vue";
import ChevronDownIcon from "../icons/ChevronDownIcon.vue";
import { QuestionMarkCircleIcon } from "../icons";
import CustomTooltip from "../common/CustomTooltip.vue";

const route = useRoute();
const user = ref(null);
const email = ref(null);
const isEdit = ref(false);
const isLoading = ref(false);
const errorMessage = ref(null);
const oldPassword = ref("");
const newPassword = ref("");
const confirmNewPassword = ref("");
const isDeleteAccount = ref(false);
const errorDisconnect = ref(null);
const ssoErrorMessage = ref(null);
const showEmailRequiredWarning = ref(false);
const disconnectAccountType = ref("Email");
const showPasswordChangeConfirmation = ref(false);
const showAddEmailModal = ref(false);
const showChangeEmailModal = ref(false);
const showPasswordForm = ref(false);
const changePasswordOldPassword = ref("");
const changePasswordNewPassword = ref("");
const changePasswordConfirm = ref("");
const isChangePasswordError = ref(false);
const isChangePasswordConfirmError = ref(false);
const changePasswordErrorMessage = ref("");
const changePasswordConfirmErrorMessage = ref("");

const changePasswordValidations = ref({
  lowercase: false,
  uppercase: false,
  specialChar: false,
  length: false,
});

const notify = inject("notify");

const regularAccounts = computed(() => {
  return (
    user.value?.accounts?.filter((account) => account.source === "REGULAR") ||
    []
  );
});

const googleAccounts = computed(() => {
  return (
    user.value?.accounts?.filter((account) => account.source === "GOOGLE") || []
  );
});

const primaryAccount = computed(() => {
  if (!user.value?.accounts?.length) {
    return null;
  }
  return user.value.accounts.find((account) => account.is_primary) || null;
});

const primaryEmailIcon = computed(() => {
  if (!primaryAccount.value) {
    return "/images/email-icon.svg";
  }
  return primaryAccount.value.source === "GOOGLE"
    ? "/images/google-logo.svg"
    : "/images/email-icon.svg";
});

const isConnectedEmail = computed(() => {
  const isConnected = regularAccounts.value.length > 0;
  return {
    isConnected,
    email: regularAccounts.value[0]?.email,
  };
});

const isGoogleConnected = computed(() => {
  const isConnected = googleAccounts.value.length > 0;
  return {
    isConnected,
    email: googleAccounts.value[0]?.email || user.value?.email,
  };
});

const canDisconnectGoogle = computed(() => {
  // Allow disconnecting Google account if there are more than 1 accounts total
  // and the Google account is not the primary account
  if (
    !isGoogleConnected.value.isConnected ||
    user.value?.accounts?.length <= 1
  ) {
    return false;
  }
  const googleAccount = googleAccounts.value[0];
  return googleAccount && !googleAccount.is_primary;
});

const canDisconnectEmail = computed(() => {
  // Allow disconnecting Email/Regular account if there are more than 1 accounts total
  // and the Email account is not the primary account
  if (
    !isConnectedEmail.value.isConnected ||
    user.value?.accounts?.length <= 1
  ) {
    return false;
  }
  const emailAccount = regularAccounts.value[0];
  return emailAccount && !emailAccount.is_primary;
});

const isGooglePrimaryAndDisabled = computed(() => {
  if (!isGoogleConnected.value.isConnected) return false;
  const googleAccount = googleAccounts.value[0];
  return (
    googleAccount?.is_primary &&
    isGoogleConnected.value.isConnected &&
    user.value?.accounts?.length > 1
  );
});

const isEmailPrimaryAndDisabled = computed(() => {
  if (!isConnectedEmail.value.isConnected) return false;
  const emailAccount = regularAccounts.value[0];
  return (
    emailAccount?.is_primary &&
    isConnectedEmail.value.isConnected &&
    user.value?.accounts?.length > 1
  );
});

const googleAccountStatusText = computed(() => {
  if (isGoogleConnected.value.isConnected) {
    return isGoogleConnected.value.email || user.value?.email || "";
  }
  return "No Google Account Linked Yet.";
});

const emailAccountStatusText = computed(() => {
  if (isConnectedEmail.value.isConnected) {
    return isConnectedEmail.value.email || user.value?.email || "";
  }
  return "No Email Account Linked Yet.";
});

const passwordChangeConfirmationMessage = computed(() => {
  if (user.value?.has_password) {
    return "Are you sure want to change your password?";
  }
  return "Are you sure want to set your password?";
});

const hasAvailableAccounts = computed(() => {
  const accounts = user.value?.accounts || [];
  if (accounts.length <= 1) {
    return false;
  }

  // If there are exactly 2 accounts, check if they have the same email
  if (accounts.length === 2) {
    const email1 = accounts[0]?.email?.toLowerCase()?.trim();
    const email2 = accounts[1]?.email?.toLowerCase()?.trim();
    if (email1 && email2 && email1 === email2) {
      return false;
    }
  }

  return true;
});

const updateUserProfile = async () => {
  try {
    const authToken = await cookiesService.getCookie("authToken");
    if (authToken) {
      const response = await userService.getProfile(authToken.token);
      if (response.status === 200) {
        user.value = response?.data?.data;
        // Set email from primary account if available, otherwise fallback to user.email
        const primaryAcc = user.value?.accounts?.find(
          (account) => account.is_primary
        );
        if (primaryAcc?.email) {
          email.value = primaryAcc.email;
        } else if (user.value?.email) {
          email.value = user.value.email;
        }
        cookiesService.setCookie("authToken", authToken);
      }
    }
  } catch (error) {
    console.error("Failed to load profile:", error);
  }
};

const isPasswordError = ref(false);
const isConfirmPasswordError = ref(false);
const passwordErrorMessage = ref("");
const confirmPasswordErrorMessage = ref("");

const validations = ref({
  lowercase: false,
  uppercase: false,
  specialChar: false,
  length: false,
});

function validatePasswordInput() {
  const result = validatePassword(newPassword.value);
  validations.value = result.validations;

  // Update error state
  isPasswordError.value = !result.isValid;
}

const validatePasswordForm = () => {
  const passwordValidationResult = validatePassword(newPassword.value);
  if (!passwordValidationResult.isValid) {
    isPasswordError.value = true;
    passwordErrorMessage.value = passwordValidationResult.message;
    return false;
  }

  // Validate confirm password
  if (!confirmNewPassword.value) {
    isConfirmPasswordError.value = true;
    confirmPasswordErrorMessage.value = "Please confirm your password";
    return false;
  }

  if (newPassword.value !== confirmNewPassword.value) {
    isConfirmPasswordError.value = true;
    confirmPasswordErrorMessage.value = "Password doesn’t match";
    return false;
  }

  // Clear any previous errors
  errorMessage.value = null;
  isPasswordError.value = false;
  passwordErrorMessage.value = "";
  isConfirmPasswordError.value = false;
  confirmPasswordErrorMessage.value = "";

  return true;
};

const handlePasswordChangeClick = () => {
  if (validatePasswordForm()) {
    showPasswordChangeConfirmation.value = true;
  }
};

const changePassword = async () => {
  isLoading.value = true;

  try {
    if (user.value?.has_password) {
      const payload = {
        current_password: oldPassword.value,
        password: newPassword.value,
        confirm_password: confirmNewPassword.value,
      };
      await authService.changePassword(payload);
    } else {
      const payload = {
        email: user.value?.email,
        password: newPassword.value,
        confirm_password: confirmNewPassword.value,
      };
      await userService.connectEmail(payload);
    }

    isLoading.value = false;
    oldPassword.value = "";
    newPassword.value = "";
    confirmNewPassword.value = "";
    showPasswordChangeConfirmation.value = false;

    updateUserProfile();
    isEdit.value = false;
  } catch (error) {
    console.log("err :", error);
    errorMessage.value = error?.response?.data?.message;
    isLoading.value = false;
    showPasswordChangeConfirmation.value = false;
  }
};

async function loginWithGoogle() {
  try {
    const response = await userService.connectGoogle();
    window.location.href = response.data.data.url;
  } catch (error) {
    console.log("err :", error);
  }
}

const deleteAccount = async () => {
  isLoading.value = true;

  try {
    let accountId = null;

    if (disconnectAccountType.value === "Google") {
      const googleAccount = googleAccounts.value[0];
      if (!googleAccount?.id) {
        isLoading.value = false;
        errorDisconnect.value = "Google account not found";
        notify.error("Google account not found");
        isDeleteAccount.value = false;
        return;
      }
      accountId = googleAccount.id;
    } else {
      const regularAccount = regularAccounts.value[0];
      if (!regularAccount?.id) {
        isLoading.value = false;
        errorDisconnect.value = "Email account not found";
        notify.error("Email account not found");
        isDeleteAccount.value = false;
        return;
      }
      accountId = regularAccount.id;
    }

    await userService.disconnectAccount(accountId);
    notify.success(`Profile has been changed`);

    isLoading.value = false;
    isDeleteAccount.value = false;
    updateUserProfile();
  } catch (error) {
    isDeleteAccount.value = false;
    errorDisconnect.value = error?.response?.data?.message;
    isLoading.value = false;
    notify.error(error?.response?.data?.message);
    console.log(error);
  }
};

const handleDisconnectEmailClick = () => {
  disconnectAccountType.value = "Email";
  isDeleteAccount.value = true;
};

function handleGoogleAccountClick() {
  if (isConnectedEmail.value.isConnected) {
    disconnectAccountType.value = "Google";
    isDeleteAccount.value = true;
  } else {
    showEmailRequiredWarning.value = true;
  }
}

function handleConnectEmailRedirect() {
  showEmailRequiredWarning.value = false;
  openChangeEmailModal();
}

const openChangeEmailAccountModal = () => {
  showChangeEmailModal.value = true;
};

const handleChangeEmailContinue = async (data) => {
  console.log("Change email continue:", data);
  showChangeEmailModal.value = false;
  // Refresh user profile to get updated primary email
  await updateUserProfile();
};

const openChangeEmailModal = () => {
  showAddEmailModal.value = true;
};

const handleChangeEmailSave = async (data) => {
  // Email account is already connected via the API call in AddEmailModal
  // Just refresh the user profile to get updated accounts
  await updateUserProfile();
  showAddEmailModal.value = false;
};

const togglePasswordForm = () => {
  showPasswordForm.value = !showPasswordForm.value;
  if (!showPasswordForm.value) {
    changePasswordOldPassword.value = "";
    changePasswordNewPassword.value = "";
    changePasswordConfirm.value = "";
    isChangePasswordError.value = false;
    isChangePasswordConfirmError.value = false;
    changePasswordErrorMessage.value = "";
    changePasswordConfirmErrorMessage.value = "";
    changePasswordValidations.value = {
      lowercase: false,
      uppercase: false,
      specialChar: false,
      length: false,
    };
  }
};

const validateChangePasswordInput = () => {
  const result = validatePassword(changePasswordNewPassword.value);
  changePasswordValidations.value = result.validations;
  isChangePasswordError.value = !result.isValid;

  if (changePasswordConfirm.value) {
    validateChangePasswordConfirm();
  }
};

const validateChangePasswordConfirm = () => {
  if (!changePasswordConfirm.value) {
    isChangePasswordConfirmError.value = false;
    changePasswordConfirmErrorMessage.value = "";
    return;
  }

  if (changePasswordNewPassword.value !== changePasswordConfirm.value) {
    isChangePasswordConfirmError.value = true;
    changePasswordConfirmErrorMessage.value = "Password doesn’t match";
  } else {
    isChangePasswordConfirmError.value = false;
    changePasswordConfirmErrorMessage.value = "";
  }
};

const validateChangePasswordForm = () => {
  const passwordValidationResult = validatePassword(
    changePasswordNewPassword.value
  );
  if (!passwordValidationResult.isValid) {
    isChangePasswordError.value = true;
    changePasswordErrorMessage.value = passwordValidationResult.message;
    return false;
  }

  if (!changePasswordConfirm.value) {
    isChangePasswordConfirmError.value = true;
    changePasswordConfirmErrorMessage.value = "Please confirm your password";
    return false;
  }

  if (changePasswordNewPassword.value !== changePasswordConfirm.value) {
    isChangePasswordConfirmError.value = true;
    changePasswordConfirmErrorMessage.value = "Password doesn’t match";
    return false;
  }

  isChangePasswordError.value = false;
  changePasswordErrorMessage.value = "";
  isChangePasswordConfirmError.value = false;
  changePasswordConfirmErrorMessage.value = "";

  return true;
};

const handleChangePasswordSave = async () => {
  if (!validateChangePasswordForm()) {
    return;
  }

  isLoading.value = true;
  changePasswordErrorMessage.value = "";

  try {
    const payload = {
      email: isConnectedEmail.value?.email,
      current_password: changePasswordOldPassword.value,
      password: changePasswordNewPassword.value,
      confirm_password: changePasswordConfirm.value,
    };

    await authService.changePassword(payload);

    isLoading.value = false;
    changePasswordOldPassword.value = "";
    changePasswordNewPassword.value = "";
    changePasswordConfirm.value = "";
    showPasswordForm.value = false;
    changePasswordValidations.value = {
      lowercase: false,
      uppercase: false,
      specialChar: false,
      length: false,
    };

    updateUserProfile();
    notify.success("Profile has been changed");
  } catch (error) {
    console.log("err :", error);
    changePasswordErrorMessage.value =
      error?.response?.data?.message || "Failed to change password";
    isLoading.value = false;
  }
};

onMounted(() => {
  ssoErrorMessage.value = route.query["error-message"];
  updateUserProfile();
});
</script>

<style scoped>
.security-settings-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.edit-mode-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.email-password-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.password-fields-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.password-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

:deep(.password-input.input-main) {
  width: 100% !important;
}

.error-message-text {
  color: #e00269;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  margin-top: 8px;
}

.security-action-button {
  height: 56px;
}

.plus-icon {
  width: 24px;
  height: 24px;
}

.disconnect-button {
  border-color: var(--error-red);
  color: var(--error-red);
}

.disconnect-button:hover:not(:disabled) {
  background-color: #f2f3fb;
}

.disconnect-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.disconnect-icon {
  width: 24px;
  height: 24px;
}

.edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  width: 100%;
}

.action-button-cancel,
.action-button-save {
  min-width: 150px;
}

.non-edit-mode-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.primary-email-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.info-banner {
  background-color: #fef7ee;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
}

.info-banner-content {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.info-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.info-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
  flex: 1;
}

.account-container {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
}

.account-field {
  flex: 1;
  background-color: #f2f3fb;
  border: 1px solid #98a2b3;
  border-radius: 8px;
  height: 56px;
  padding: 4px 16px;
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 0;
}

.account-field-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.account-field-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.change-account-button {
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  height: 56px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: transparent;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--primary-blue);
  white-space: nowrap;
  transition: background-color 0.2s ease;
}

.change-account-button:hover:not(:disabled) {
  background-color: #f2f3fb;
}

.change-account-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sign-in-methods-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.section-header-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.035px;
  color: #e00069;
  margin: 0;
}

.section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-xxl);
  line-height: 100%;
  color: #e00069;
  margin: 0;
}

.field-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.field-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.field-helper-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.035px;
  color: #e00069;
  margin: 0;
}

.password-change-container {
  display: flex;
  gap: 8px;
}

.password-input-wrapper {
  position: relative;
}

.password-input {
  width: 100%;
}

.password-icon-overlay {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-icon {
  width: 24px;
  height: 24px;
}

.section-divider {
  height: 1px;
  background-color: #d8dae5;
  width: 100%;
}

.connected-accounts-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.account-divider {
  height: 1px;
  background-color: #d8dae5;
  width: 100%;
}

.sso-error-message {
  color: #e00069;
  font-size: var(--fs-medium);
  font-weight: 400;
  margin-top: 12px;
  font-family: "League Spartan", sans-serif;
}

.password-form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 16px;
}

.password-info-banner {
  background-color: #fef7ee;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
}

.password-form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.password-form-actions {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

:deep(.action-button__icon--right .chevron-rotated),
:deep(.chevron-rotated) {
  display: inline-block;
  transition: transform 0.3s ease;
}

:deep(.action-button__icon--right .chevron-rotated svg),
:deep(.chevron-rotated svg) {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

@media (max-width: 768px) {
  .account-container {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .section-title {
    font-size: var(--fs-large);
    line-height: 28px;
    font-weight: 500;
  }

  .field-label {
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    font-weight: 400;
  }

  .field-helper-text {
    font-size: var(--fs-small);
    line-height: 20px;
    letter-spacing: 0.035px;
    font-weight: 400;
  }

  .field-container :deep(.input-label) {
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    color: #3b4648;
    margin-bottom: 4px;
  }

  .field-container :deep(.input-main) {
    height: 40px;
    padding: 4px 16px;
    border-radius: 8px;
    border: 1px solid #98a2b3;
    font-family: "League Spartan", sans-serif;
    font-weight: 500;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    color: #3b4648;
    box-sizing: border-box;
  }

  .field-container :deep(.input-main:disabled) {
    background-color: #f2f3fb;
    border-color: #98a2b3;
  }

  .field-container :deep(.input-main:not(:disabled)) {
    background-color: #ffffff;
    border-color: #6c737f;
  }

  .account-field {
    height: 40px;
    padding: 8px 16px;
    width: 100%;
  }

  .password-change-container {
    flex-direction: column-reverse;
    gap: 8px;
    width: 100%;
  }

  .account-field-text {
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    font-weight: 500;
  }

  .security-action-button {
    width: 100%;
    height: 40px;
  }

  :deep(.tt-trigger) {
    width: 100%;
  }

  .edit-actions {
    flex-direction: row;
    gap: 8px;
  }

  .action-button-cancel,
  .action-button-save {
    flex: 1;
    min-height: 40px;
    height: 40px;
    font-size: var(--fs-small);
    line-height: 20px;
    letter-spacing: 0.014px;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 700;
  }
}
</style>
